/**
 * SourceFilters - the segmented control and the chip rows.
 *
 * TWO SECTIONS, NOT TEN TABS. The segmented control asks the only structural
 * question that matters - our paper, or the law we are arguing from - and
 * everything that used to be a tab is a chip inside the second one.
 *
 * A chip for a corpus this jurisdiction does not publish still RENDERS, muted,
 * so the taxonomy is legible; picking it explains itself instead of showing an
 * empty list that reads as a bug. That is the "not available here" the old
 * tabs never managed: they simply did not exist, so you could not tell a
 * jurisdiction without preparatory works from a matter that had none yet.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { PiFolderOpenLight, PiScalesLight } from 'react-icons/pi';
import {
  AUTHORITY_CORPORA,
  CORPUS_LABELS,
  SourceCorpus,
  SourceOrigin,
  SourceSection,
} from './sourceModel';

/* ── The two sections ─────────────────────────────────────────────── */

interface SegmentProps {
  section: SourceSection;
  counts: Record<SourceSection, number>;
  onChange: (section: SourceSection) => void;
}

export const SectionSegments: React.FC<SegmentProps> = ({ section, counts, onChange }) => {
  const { t } = useTranslation();

  const segments: Array<{ id: SourceSection; label: string; icon: React.ReactNode }> = [
    { id: 'case_files', label: t('Case files'), icon: <PiFolderOpenLight className="h-3.5 w-3.5" /> },
    { id: 'legal_authority', label: t('Legal authority'), icon: <PiScalesLight className="h-3.5 w-3.5" /> },
  ];

  return (
    <div
      role="tablist"
      aria-label={t('Documents')}
      data-testid="source-sections"
      className="glass-bar inline-flex items-center gap-0.5 rounded-full p-0.5 border border-hairline-soft shadow-sm"
    >
      {segments.map((seg) => {
        const active = seg.id === section;
        return (
          <button
            key={seg.id}
            type="button"
            role="tab"
            aria-selected={active}
            data-testid={`source-section-${seg.id}`}
            data-active={active}
            onClick={() => onChange(seg.id)}
            className={clsx(
              'nav-pill flex items-center gap-1.5 rounded-full px-3 py-1',
              'text-xs font-medium transition-colors duration-200 ease-slide',
              active ? 'nav-pill-active text-fg-strong' : 'text-fg-muted hover:text-fg-body'
            )}
          >
            {seg.icon}
            <span>{seg.label}</span>
            <span className="tabular-nums text-[10px] text-fg-muted">{counts[seg.id]}</span>
          </button>
        );
      })}
    </div>
  );
};

/* ── Corpus chips (Legal authority only) ──────────────────────────── */

interface CorpusChipsProps {
  /** `null` is the "All" chip. */
  corpus: SourceCorpus | null;
  counts: Record<SourceCorpus, number>;
  total: number;
  /** False → the jurisdiction publishes nothing for this corpus. */
  available: Record<SourceCorpus, boolean>;
  onChange: (corpus: SourceCorpus | null) => void;
}

export const CorpusChips: React.FC<CorpusChipsProps> = ({
  corpus,
  counts,
  total,
  available,
  onChange,
}) => {
  const { t } = useTranslation();

  const chip = (
    key: string,
    label: string,
    count: number | null,
    active: boolean,
    unavailable: boolean,
    onClick: () => void
  ) => (
    <button
      key={key}
      type="button"
      data-testid={`corpus-chip-${key}`}
      data-active={active}
      data-unavailable={unavailable}
      // Still clickable when unavailable: the panel explains why it is empty,
      // which a disabled chip could not do.
      onClick={onClick}
      title={unavailable ? t('Not available in this jurisdiction') : undefined}
      className={clsx(
        'rounded-full border px-2.5 py-1 text-[11px] font-medium',
        'transition-colors duration-200 ease-slide whitespace-nowrap',
        active
          ? 'border-champagne/50 bg-champagne/10 text-fg-strong'
          : unavailable
          ? 'border-dashed border-hairline-soft text-fg-muted/60 hover:text-fg-muted'
          : 'border-hairline-soft text-fg-muted hover:text-fg-body hover:border-hairline'
      )}
    >
      {label}
      {count !== null && count > 0 && (
        <span className="ms-1 tabular-nums text-[10px] opacity-70">{count}</span>
      )}
    </button>
  );

  return (
    <div
      role="group"
      aria-label={t('Source type')}
      data-testid="corpus-chips"
      className="flex flex-wrap items-center gap-1.5"
    >
      {chip('all', t('All'), total, corpus === null, false, () => onChange(null))}
      {AUTHORITY_CORPORA.map((id) =>
        chip(
          id,
          t(CORPUS_LABELS[id]),
          counts[id],
          corpus === id,
          !available[id],
          () => onChange(corpus === id ? null : id)
        )
      )}
    </div>
  );
};

/* ── Origin filter ────────────────────────────────────────────────── */

interface OriginFilterProps {
  origin: SourceOrigin | null;
  counts: Record<SourceOrigin, number>;
  onChange: (origin: SourceOrigin | null) => void;
}

export const OriginFilter: React.FC<OriginFilterProps> = ({ origin, counts, onChange }) => {
  const { t } = useTranslation();

  const options: Array<{ id: SourceOrigin; label: string; tint: string }> = [
    { id: 'uploaded', label: t('Uploaded'), tint: 'border-champagne/50 bg-champagne/10 text-champagne' },
    {
      id: 'ai_found',
      label: t('AI-found'),
      tint: 'border-info-300 dark:border-info-700 bg-info-50 dark:bg-info-900/20 text-info-700 dark:text-info-300',
    },
  ];

  return (
    <div
      role="group"
      aria-label={t('Origin')}
      data-testid="origin-filter"
      className="flex items-center gap-1.5"
    >
      {options.map((opt) => {
        const active = origin === opt.id;
        return (
          <button
            key={opt.id}
            type="button"
            data-testid={`origin-filter-${opt.id}`}
            data-active={active}
            onClick={() => onChange(active ? null : opt.id)}
            className={clsx(
              'rounded-full border px-2.5 py-1 text-[11px] font-medium',
              'transition-colors duration-200 ease-slide whitespace-nowrap',
              active ? opt.tint : 'border-hairline-soft text-fg-muted hover:text-fg-body'
            )}
          >
            {opt.label}
            {counts[opt.id] > 0 && (
              <span className="ms-1 tabular-nums text-[10px] opacity-70">{counts[opt.id]}</span>
            )}
          </button>
        );
      })}
    </div>
  );
};
